import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, FileText, FileSignature, Inbox, Loader2 } from "lucide-react";
import { API_BASE } from "../../components/AuthContext";

function fmtBytes(n) {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function fmtDate(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
  catch { return s; }
}

function sourceLabel(d) {
  if (d.source === "esign") return { label: "E-signed", icon: <FileSignature size={11} /> };
  if (d.source === "upload") return { label: "Uploaded", icon: <Inbox size={11} /> };
  return { label: "Document", icon: <FileText size={11} /> };
}

export default function PortalDocuments() {
  const [docs, setDocs] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API_BASE}/portal/documents`);
        setDocs(r.data || []);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Could not load documents");
        setDocs([]);
      }
    })();
  }, []);

  const download = async (d) => {
    try {
      const r = await axios.get(
        `${API_BASE}/portal/documents/${d.document_id}/download`,
        { responseType: "blob" },
      );
      const blob = new Blob([r.data], { type: d.content_type || "application/octet-stream" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = d.filename || "document";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Download failed");
    }
  };

  if (docs === null) {
    return (
      <div className="p-6 text-[var(--ink-3)]">
        <Loader2 size={14} className="inline animate-spin mr-2"/> Loading documents…
      </div>
    );
  }

  return (
    <div className="px-10 py-12 max-w-[1100px]" data-testid="portal-documents-page">
      <div className="mb-8">
        <div className="overline mb-2 text-[var(--accent)]">Shared with you</div>
        <h1 className="font-heading text-4xl sm:text-5xl leading-tight mb-2" style={{ fontWeight: 500 }}>
          My <em className="italic" style={{ color: "var(--accent)" }}>documents</em>
        </h1>
        <p className="text-[var(--ink-2)] max-w-2xl">
          Signed engagements, lodged returns, and other files your accountant has shared with you.
        </p>
      </div>

      {docs.length === 0 ? (
        <div className="card p-12 text-center">
          <FileText size={32} className="mx-auto text-[var(--ink-3)] mb-3" />
          <div className="text-[14px] text-[var(--ink-2)]">No documents yet.</div>
          <div className="text-[12px] text-[var(--ink-3)] mt-1">
            When your accountant shares a signed document or completes a return, it will land here.
          </div>
        </div>
      ) : (
        <div className="card divide-y divide-[var(--border)]">
          {docs.map((d) => {
            const src = sourceLabel(d);
            return (
              <div
                key={d.document_id}
                className="flex items-center justify-between gap-4 p-4 hover:bg-[var(--subtle)]"
                data-testid={`portal-doc-${d.document_id}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="flex-shrink-0 w-10 h-10 rounded-full bg-[var(--subtle)] flex items-center justify-center text-[var(--ink-3)]">
                    {src.icon}
                  </span>
                  <div className="min-w-0">
                    <div className="font-medium text-[var(--ink)] truncate">{d.filename}</div>
                    <div className="text-[11px] text-[var(--ink-3)] font-mono">
                      {src.label} · {fmtDate(d.signed_at || d.uploaded_at)} · {fmtBytes(d.size)}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => download(d)}
                  className="btn-secondary flex-shrink-0"
                  data-testid={`portal-doc-download-${d.document_id}`}
                >
                  <Download size={13}/> Download
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
